import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings'
import { incrementQuickCounter } from '@mobi/utils/logging/quickCounter'
import type { Contract } from '@mobi/web-native-comms'
import { isReactNativeApp, sendToNative, subscribeToNative } from '@mobi/web-native-comms/web'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { HidePopup, ShowPopup } from '@core/Components/Popup/driver'

export function ShowNotificationAuthPopup({
  reason,
  notificationAuthStatus,
}: {
  reason: string
  notificationAuthStatus: Contract['RESPONSE_PUSH_AUTH_STATUS']['status']
}): void {
  ShowPopup({
    content: NotificationAuthPopup,
    contentProps: { reason, notificationAuthStatus },
  })
}

export function checkNotificationStatusAsync(): Promise<Contract['RESPONSE_PUSH_AUTH_STATUS']> {
  const promise = new Promise<Contract['RESPONSE_PUSH_AUTH_STATUS']>(resolve => {
    if (isReactNativeApp) {
      const { dispose } = subscribeToNative('RESPONSE_PUSH_AUTH_STATUS', payload => {
        dispose()
        resolve(payload)
      })
      sendToNative('REQUEST_PUSH_AUTH_STATUS')
    } else if (process.env.NODE_ENV === 'development') {
      resolve({
        status: 'Authorized',
        fcmRegistrationToken: 'mock',
        deviceId: 'mock-device-id',
      })
    } else {
      resolve({ status: 'NotDetermined' })
    }
  })
  return promise
}

const NotificationAuthPopup = ({
  reason,
  notificationAuthStatus,
}: {
  reason: string
  notificationAuthStatus: Contract['RESPONSE_PUSH_AUTH_STATUS']['status']
}): JSX.Element => {
  const enableHandler = notificationAuthStatus === 'NotDetermined' ? hardReprompt : openSettings

  useEffect(() => {
    const statusKey = notificationAuthStatus === 'NotDetermined' ? 'request' : 'settings'
    incrementQuickCounter('popup-push-notif-perms')
    incrementQuickCounter(`popup-push-notif-perms-${statusKey}`)
  }, [])

  return (
    <NotificationAuthPopupContainerStyled>
      <IconStyled type='pushNotification' size='4rem' color={hexColors.chocolate} />
      <NotificationAuthPopupTitleStyled>
        <strong>Enable Notifications</strong>
      </NotificationAuthPopupTitleStyled>
      <NotificationAuthDescriptionStyled>{reason}</NotificationAuthDescriptionStyled>
      <ButtonStyled>
        <ButtonBlock color='primary' onClick={enableHandler}>
          {notificationAuthStatus === 'NotDetermined' ? 'Enable Notifications' : 'Open Settings'}
        </ButtonBlock>
      </ButtonStyled>
      <ButtonStyled>
        <ButtonBlock color='secondary' onClick={HidePopup}>
          Maybe Later
        </ButtonBlock>
      </ButtonStyled>
    </NotificationAuthPopupContainerStyled>
  )
}

function hardReprompt() {
  if (isReactNativeApp) {
    sendToNative('SHOW_PUSH_AUTH')
  }
  HidePopup()
}

function openSettings() {
  if (isReactNativeApp) {
    const { dispose } = subscribeToNative('USER_OPENED_NOTIFICATION_SETTINGS', () => {
      dispose()
      HidePopup()
    })
    sendToNative('UPDATE_PUSH_NOTIFICATION_SETTINGS')
  }
}

const IconStyled = styled(Icon)({
  margin: '3rem 0rem 2rem',
})

const NotificationAuthPopupTitleStyled = styled.div({
  fontSize: '2rem',
})

const NotificationAuthDescriptionStyled = styled.div({
  fontSize: '1.4rem',
  width: '90%',
  margin: '1rem 0',
  textAlign: 'center',
})

const ButtonStyled = styled.div({
  fontSize: '1.4rem',
  width: '92%',
  margin: '1rem 0',
})

const NotificationAuthPopupContainerStyled = styled.div({
  top: '50%',
  minHeight: '20rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})
